import type { ToolDefinition, ToolType } from "../types";

const IMAGE_ANALYZER_TOOL_TYPE = {
  label: "Free Analysis Tool",
  icon: "ScanSearch",
} as const satisfies ToolType;

const imageAnalyzerTool: ToolDefinition = {
  slug: "image-analyzer",
  // GSC v2: "quality" cluster is the biggest untapped opportunity — "image quality checker"
  // (172 imp, pos 17.5), "image quality analyzer" (164 imp), "picture quality analyzer"
  // (140 imp) all sitting on page 2 with ~0 clicks. Front-load "Image Quality Checker"
  // while keeping "Free", "Analyzer", "Online", "EXIF Viewer" all present.
  name: "Free Image Quality Checker & Analyzer Online | EXIF Viewer",
  // GSC v2: H1 leads with the highest-impression near-page-1 term ("image quality checker"),
  // keeps "analyzer" + "metadata reader" (KP 1K–10K "image metadata reader"). Removed the
  // awkward double "&" from the original.
  title: "Image Quality Checker, Analyzer & EXIF Metadata Reader",
  // GSC v2: meta description is the CTR lever for terms already on page 1 (image format checker
  // pos 5.6, image inspector pos 6.6). Added "quality checker" + "test photo quality" (tester
  // cluster ~215 imp, previously absent) without dropping any working format/metadata signals.
  description:
    "Free online image quality checker and analyzer. Test photo quality, DPI, dimensions, format, and hidden EXIF metadata for JPG, PNG, WebP & more. No account required.",
  // GSC v2: surfaced "image quality tester" in the visible hero line (tester cluster had zero
  // coverage anywhere on the page). Kept the "free", JPG analyzer, PNG inspector, palette,
  // and forensics signals intact.
  longDescription:
    "Upload any image to instantly check its quality, dimensions, DPI, file format, and hidden EXIF metadata; all free, no account required. Works as an image quality tester, JPG analyzer, PNG inspector, color palette extractor, and photo forensics tool in a single pass.",
  toolType: {
    label: "Image Inspector",
    icon: "ScanSearch",
  },
  heroBadges: [
    "100% Free",
    "No Account Required",
    "Check Image Quality",
    "View Hidden Metadata",
    "Extract Color Palettes",
    "Check DPI Instantly",
  ],
  category: "Analysis Tools",
  categorySlug: "image-analysis-tools",
  href: "/tools/image-analyzer",
  api: "/api/v1/analyze",
  // KEYWORDS: unchanged metakeywords per rules; all retained as relevant
  keywords: [
    "snappy image property analyzer",
    "extract color palette from photo",
    "free exif data viewer app",
    "how to check picture resolution",
    "determine file format online",
    "printer quality inspector software",
    "website scheme generator tool",
    "dimension and width checker",
    "convert logo into hex codes",
    "discover hidden metadata tags",
  ],
  pageContent: {
    sections: {
      steps: {
        title: "How to Analyze an Image Online",
        description:
          "Follow these simple steps to check image quality, read metadata, and extract color palettes instantly; no software needed.",
      },
      benefits: {
        // GSC v2: section title now anchors "image quality checker" (172 imp) AND
        // "picture quality analyzer" (140 imp, pos 29.7) — the second-highest impression term
        // in the dataset, previously only hinted at via "Picture Inspector".
        title:
          "Why Use Our Free Image Quality Checker and Picture Quality Analyzer?",
        description:
          "Understand exactly what is inside your image files before you print, publish, or share them — no Photoshop required.",
      },
      faqs: {
        title: "Frequently Asked Questions",
        // GSC v2: added "test image quality" to the FAQ intent signal alongside existing terms.
        description:
          "Common questions about checking and testing image quality online, reading EXIF data, analyzing JPG and PNG files, and understanding DPI.",
      },
    },
    introBlocks: [
      {
        kind: "text",
        // GSC v2: H2 now anchors the three biggest quality terms — "image quality checker"
        // (172), "image quality tester" (81), "picture quality" framing — plus "property"
        // for the page-1 "image property checker" term. This is the dominant cluster.
        title: "Image Quality Checker, Tester & Property Analyzer in One Tool",
        level: "h2",
        paragraphs: [
          // GSC v2: opening paragraph now carries "image quality check" (172/68/62/60 imp across
          // variants), "picture quality" (140 imp), and "image quality tester" (81 imp) naturally,
          // instead of only "image quality analyzer". These are the queries Google ranks the page
          // for on page 2 — they need stronger body prominence to move up.
          "Most image editors tell you what you can do to a photo — this tool tells you what is already inside it. Upload any supported file to run a full image quality check in seconds: exact pixel dimensions, true file format, DPI density, brightness and contrast readings, and the complete hidden EXIF metadata block. Use it as a quick image quality tester before sending a file to a commercial printer, or as a picture quality checker to confirm a photo is sharp and high-resolution enough to publish — the results appear on the same page within seconds.",
          // CHANGE: "free image analyzer" (pos 9.1, 10 imp), "image analyzer free" (pos 7.5, 10 imp),
          // "free image analysis" (pos 6, 2 imp), "free image analysis tools" (pos 6.5, 2 imp)
          // — all near page 1 but zero clicks; "free" signal strengthened in intro body
          "This is a fully free image analyzer — no account, no watermark, no upload limit per session. It works on JPG, PNG, WebP, GIF, BMP, TIFF, ICO, and AVIF files, and returns structured data covering every major image property in a single analysis pass.",
        ],
        note: "This tool returns structured analysis data. It does not alter or transform your original image file.",
      },
      {
        kind: "callouts",
        groups: [
          {
            // CHANGE: "check image properties online" (pos 9.33, 3 imp) + "check image format online"
            // (pos 8.33, 3 imp) + "image file analysis" (pos 6, 2 imp) — all near page 1, zero clicks
            // — surfaced as callout labels to give these terms heading-level prominence
            title: "What Gets Checked",
            layout: "list",
            iconColor: "primary",
            items: [
              {
                // CHANGE: "check image properties online" (pos 9.33) + "image properties checker"
                // (pos 8, 1 imp) anchored in label
                label: "Image Properties & Dimensions",
                icon: "Maximize",
                description:
                  "Check image width, height, exact aspect ratio, file size, and the true underlying format — including detecting if a file has been falsely renamed.",
                accent: "primary",
              },
              {
                // GSC v2: label now reads as both a checker and a tester — "image quality tester"
                // (81 imp) + "photo quality tester" (59 imp) + "dpi image analyzer" (29 imp).
                label: "Image Quality Tester & DPI Check",
                icon: "Printer",
                description:
                  "Test image quality and run a DPI image analyzer check to verify whether your file meets 72 DPI web standards or 300 DPI print requirements — no Photoshop needed.",
                accent: "primary",
              },
              {
                // CHANGE: "photo forensics online free" (pos 11.33, 3 imp) — near page 1 but
                // not surfaced at all in the original JSON; added as explicit callout item
                label: "Hidden EXIF Metadata & Photo Forensics",
                icon: "Database",
                description:
                  "View camera model, lens, capture date, software tags, GPS coordinates, and copyright fields embedded in the file — useful for photo forensics online and privacy checks.",
                accent: "primary",
              },
              {
                // CHANGE: "image format detector online" (pos 17.67, 3 imp) — missing from original
                label: "Image Format Detector",
                icon: "FileSearch",
                description:
                  "Determine the true file format online even when a file extension has been changed. Detects JPG, PNG, WebP, GIF, BMP, TIFF, ICO, and AVIF by reading the file header directly.",
                accent: "primary",
              },
            ],
          },
        ],
      },
      {
        kind: "text",
        // CHANGE: H2 now targets "jpg analyzer" (79 imp, pos 50) + "jpeg analyzer" (10 imp, pos 11)
        // — combined 89 impressions for format-specific analyzer terms, barely addressed in original
        title: "JPG Analyzer, PNG Inspector, and Color Palette Extractor",
        level: "h2",
        paragraphs: [
          // CHANGE: "jpg analyzer" (79 imp), "jpeg analyzer" (pos 11.8, 10 imp), "analyze png"
          // (pos 18, 3 imp), "png analyzer" (6 imp, 1 click) — format-specific terms pulled together
          "Different file formats store different amounts of data. Our JPG analyzer reads the compression markers, color space, and full EXIF block embedded by your camera app. The PNG inspector checks the metadata chunk and color profile. For WebP and AVIF, the tool reads the container headers to surface whatever properties the encoder wrote. Regardless of format, you get the same structured breakdown — making it a reliable image format detector online for all common raster types.",
          // CHANGE: "image content analysis tool" (pos 9.5, 2 imp) + "image size analyzer"
          // (pos 18, 5 imp) + color palette cluster from Keyword Planner
          "The visual analysis layer goes beyond technical headers. Acting as a color palette generator from your uploaded image, it scans the actual pixel data to output dominant colors and accent shades with their hex codes — ready to paste directly into Figma, CSS, or a brand kit. The image size analyzer portion confirms exact pixel counts so you can plan layout slots, social media crops, or print bleed areas with confidence.",
        ],
      },
      {
        kind: "table",
        tables: [
          {
            // CHANGE: table covers the quality-check angle explicitly — "image quality tester" (81 imp),
            // "image quality test online" (49 imp), "photo quality checking tool" (72 imp),
            // "check image quality" cluster collectively.
            title: "What the Analysis Output Covers",
            description:
              "A guide to the data categories returned and how each one helps you check or test image quality, properties, and metadata.",
            columns: ["Data Category", "What It Reveals", "Primary Use Case"],
            rows: [
              {
                "Data Category": "Basic Properties",
                "What It Reveals":
                  "Width, height, aspect ratio, file size, true format",
                "Primary Use Case":
                  "Check image properties online before upload or print.",
              },
              {
                "Data Category": "Quality & DPI",
                "What It Reveals": "DPI density, brightness, contrast readings",
                "Primary Use Case":
                  "Image quality checker and tester for web (72 DPI) vs print (300 DPI).",
              },
              {
                "Data Category": "Color Palette",
                "What It Reveals":
                  "Dominant colors and hex codes from pixel scan",
                "Primary Use Case":
                  "Extract color palette for brand kits and design files.",
              },
              {
                "Data Category": "EXIF Metadata",
                "What It Reveals":
                  "Camera model, lens, date, GPS, software tags",
                "Primary Use Case":
                  "Photo forensics, copyright checks, and privacy audits.",
              },
            ],
            note: "Images downloaded from social media often have EXIF data stripped. An empty metadata section is normal for those files.",
            emphasisColumns: ["Data Category"],
            monospaceColumns: [],
          },
        ],
      },
    ],
    steps: [
      {
        number: "01",
        title: "Upload Your Image",
        // CHANGE: "jpg analyzer" / "jpeg analyzer" / "png analyzer" explicitly named in step
        description:
          "Drag and drop a supported raster image or click to browse. Accepted formats include JPG, PNG, WebP, GIF, BMP, TIFF, ICO, and AVIF. Works as a JPG analyzer, PNG inspector, or WebP checker.",
      },
      {
        number: "02",
        title: "Click Start Full Analysis",
        // CHANGE: "image quality check" + "dpi image analyzer" + "check image properties" language
        description:
          "Run the analysis. The tool checks image quality, reads DPI data, inspects the file format, extracts the color palette, and parses all available EXIF metadata fields in one pass.",
      },
      {
        number: "03",
        title: "Review the Results",
        // CHANGE: "check image properties online" + "image content analysis tool" + quality angle
        description:
          "Review the structured breakdown on screen: image properties, quality and DPI readings, the color palette with hex codes, and any embedded EXIF or GPS metadata fields present in the file.",
      },
      {
        number: "04",
        title: "Act on the Insights",
        description:
          "Use the results to decide on next steps — resizing, format conversion, metadata cleanup, or adjusting DPI before sending to a printer. Start a new analysis on another file at any time.",
      },
    ],
    benefits: [
      {
        icon: "BarChart2",
        // GSC v2: title now spans "image quality checker" (172 imp) + "image quality tester"
        // (81 imp) + "photo quality tester" (59 imp) — three separate near-page-1 clusters that
        // previously only had "Image Quality Analyzer" coverage.
        title: "Image Quality Checker & Tester",
        description:
          "Check and test image quality online instantly — brightness levels, contrast readings, and DPI density reported together as a complete picture quality check before printing or publishing.",
      },
      {
        icon: "Search",
        // CHANGE: "check image properties online" (pos 9.33) + "image properties checker" (pos 8)
        // — near-page-1 terms anchored as benefit title for heading-level signal
        title: "Check Image Properties Online",
        description:
          "See exact pixel dimensions, true file format, aspect ratio, and file size in one report. Detects false file extensions so a WebP renamed to JPG is flagged correctly.",
      },
      {
        icon: "Palette",
        title: "Color Palette Extraction",
        description:
          "Extract dominant colors and hex codes from any uploaded photo or logo. Copy values directly into Figma, Adobe, or CSS — no manual color picking needed.",
      },
      {
        icon: "Printer",
        // CHANGE: "dpi image analyzer" (29 imp, pos 32) — elevated to dedicated benefit
        title: "DPI Image Analyzer",
        description:
          "Instantly verify if your image meets 72 DPI web standards or 300 DPI print requirements. Prevents costly reprints caused by submitting a screen-resolution file to a commercial press.",
      },
      {
        icon: "ShieldCheck",
        // CHANGE: "photo forensics online free" (pos 11.33, 3 imp) — near page 1, now in benefits
        title: "EXIF Viewer & Photo Forensics",
        description:
          "Read the full hidden metadata block — camera model, capture date, GPS coordinates, and software tags. Useful for photo forensics checks, copyright verification, and privacy audits before sharing.",
      },
    ],
    faqs: [
      {
        // GSC v2: question reworded to span "image quality checker" (172 imp) + "image quality
        // tester" (81 imp) — both the dominant terms — and answer now includes "test image quality".
        question:
          "What does an image quality checker and tester actually check?",
        answer:
          "Our image quality checker reads DPI (dots per inch) density, brightness level, and contrast range of the uploaded file. It also confirms the true pixel dimensions, which together let you test image quality and get a reliable picture quality check before printing or publishing. A file with a high pixel count but low DPI will print blurry; this tool flags that combination so you can act before it's a problem.",
      },
      {
        // CHANGE: "image quality checker online" (28 imp, pos 39) + "check image quality online"
        // (25 imp, pos 45) — second-biggest unaddressed cluster, now its own FAQ question
        question: "How do I check image quality online without Photoshop?",
        answer:
          "Upload your file to our free image quality checker. The analyzer reads the file headers and pixel data directly — no Photoshop license required. You get a DPI reading, brightness and contrast scores, exact dimensions, and the full EXIF metadata block in one report, accessible from any browser on desktop or mobile.",
      },
      {
        // GSC v2: new FAQ for the "tester" / "test" phrasing specifically — "image quality test
        // online" (49 imp), "photo quality tester" (59 imp), "test image quality" (22 imp).
        // Distinct enough from the "checker" wording above to warrant its own question.
        question: "How do I test image quality online for a photo or picture?",
        answer:
          "Upload the file and click Start Full Analysis. The tool runs an image quality test that reports DPI density, brightness, contrast, and exact pixel dimensions, then flags whether the photo is sharp enough for its intended use. It works the same way as a photo quality tester or picture quality checker — there is nothing to install and no account to create.",
      },
      {
        // CHANGE: "jpg analyzer" (79 imp, pos 50) + "jpeg analyzer" (pos 11.8, 10 imp)
        // — 89 combined impressions; format-specific FAQ added to capture this cluster
        question: "Can I use this as a JPG or JPEG analyzer?",
        answer:
          "Yes. The tool reads the internal JPEG structure including compression markers, embedded ICC color profile, and the full EXIF metadata block your camera or phone wrote at the time of capture. Upload a .jpg or .jpeg file and you will see the same structured property breakdown as any other supported format — dimensions, DPI, color palette, and metadata.",
      },
      {
        // CHANGE: "check image format online" (pos 8.33, 3 imp) + "image format detector online"
        // (pos 17.67, 3 imp) — both near page 1, missing from original FAQs entirely
        question: "How does the image format detector work?",
        answer:
          "The analyzer reads the actual file header bytes rather than trusting the file extension. This means if someone renamed a WebP file to .jpg to get it past an upload filter, the tool will report the true format as WebP. It supports detection across JPG, PNG, WebP, GIF, BMP, TIFF, ICO, and AVIF containers.",
      },
      {
        // CHANGE: "check image properties online" (pos 9.33) + "check photo details online free"
        // (pos 11.33) — both near page 1, zero clicks, need a direct FAQ answer
        question: "What image properties can I check online with this tool?",
        answer:
          "The analysis returns width and height in pixels, true file format, aspect ratio, file size, DPI density, brightness level, contrast score, a dominant color palette with hex codes, and the full embedded EXIF metadata block including camera model, capture date, GPS coordinates if present, and software tags.",
      },
      {
        // CHANGE: "photo forensics online free" (pos 11.33, 3 imp) — near page 1, absent from original
        question: "Can this tool be used for photo forensics online?",
        answer:
          "It provides the foundational data used in photo forensics: original capture date and time, camera make and model, GPS coordinates, and any software editing tags in the EXIF block. This is useful for verifying the authenticity of a photo, confirming who shot it, or checking if metadata has been stripped or altered. For deeper manipulation detection, a dedicated forensic tool would be needed.",
      },
      {
        question:
          "How can I check if my photo has 300 DPI for high-quality printing?",
        answer:
          "Upload your file and look at the DPI Info section in the results. It reads the DPI value stored in the file header. A result of 72 DPI means the file is screen-optimized and will print poorly at large sizes. A result of 300 DPI means it is ready for high-quality commercial print. If your file shows 72 DPI, resize and re-export from your editing software at 300 DPI before sending to the printer.",
      },
      {
        question:
          "Why is my EXIF data missing after uploading a photo to social media?",
        answer:
          "Most social platforms — including Facebook, Instagram, and X — automatically strip EXIF metadata on upload. This is a deliberate privacy measure to prevent unintended location sharing and reduces file sizes for faster delivery. If you download a photo from these platforms and analyze it, the metadata section will typically be empty.",
      },
      {
        question: "How do I extract a color palette from an image for Figma?",
        answer:
          "Upload your image and the analyzer scans the pixel data to identify dominant and accent colors, returning each one with its exact hex code. Copy those values directly into your Figma file, CSS variables, or brand kit. No manual eyedropper or third-party plugin required.",
      },
      {
        question: "Does this tool store or share my uploaded images?",
        answer:
          "No. Your image is processed to generate the analysis report and is not stored, indexed, or shared. The tool returns structured data on the page and the session ends cleanly when you close or refresh.",
      },
    ],
    useCases: [
      {
        label: "Print Preparation",
        icon: "Printer",
        description:
          "Run an image quality check to confirm DPI and dimensions meet your printer's requirements before sending files to a commercial press.",
        accent: "primary",
      },
      {
        label: "Brand Design",
        icon: "Paintbrush",
        description:
          "Upload a client's logo or reference image to extract the exact dominant hex color codes for a consistent brand kit.",
        accent: "primary",
      },
      {
        label: "Photo Forensics & Privacy",
        icon: "FileSearch",
        description:
          "Inspect EXIF metadata to verify the original capture date, camera model, and whether GPS location data is embedded before sharing publicly.",
        accent: "primary",
      },
    ],
    audiences: [
      {
        label: "Photographers",
        icon: "Camera",
        description:
          "Verify exported galleries retain correct copyright tags, color profiles, and DPI before client delivery.",
        accent: "accent",
      },
      {
        label: "Web Developers",
        icon: "Code",
        description:
          "Check the true file format and precise aspect ratio of assets — including detecting falsely renamed file extensions.",
        accent: "accent",
      },
      {
        label: "Privacy-Conscious Users",
        icon: "Shield",
        description:
          "Scan personal photos to uncover embedded GPS coordinates or device identifiers before posting to public platforms.",
        accent: "accent",
      },
    ],
  },
};

export default imageAnalyzerTool;
export { imageAnalyzerTool };
